<template>
  <div class="trace-chain-page">
    <a-card title="追溯链路查询">
      <a-form-model layout="inline" :model="searchForm" class="search-form">
        <a-form-model-item label="商品ID">
          <a-input v-model="searchForm.goods_id" allowClear placeholder="ERP商品ID" style="width: 140px" />
        </a-form-model-item>
        <a-form-model-item label="追溯ID">
          <a-input v-model="searchForm.product_id" allowClear placeholder="trace_product ID" style="width: 160px" />
        </a-form-model-item>
        <a-form-model-item label="批次">
          <a-input v-model="searchForm.batch" allowClear placeholder="批次号" style="width: 160px" />
        </a-form-model-item>
        <a-form-model-item label="关键词">
          <a-input-search
            v-model="searchForm.keyword"
            allowClear
            placeholder="商品名称/编号"
            style="width: 240px"
            @search="loadDetail"
          />
        </a-form-model-item>
        <a-form-model-item>
          <a-button type="primary" icon="search" :loading="loading" @click="loadDetail">查询</a-button>
        </a-form-model-item>
      </a-form-model>

      <a-empty v-if="!detail && !loading" description="请输入商品ID、追溯ID、批次或关键词查询链路" />

      <template v-if="detail">
        <a-alert
          class="summary-alert"
          :type="summaryAlertType"
          :message="summaryMessage"
          :description="summaryDescription"
          showIcon
        />

        <div class="node-grid">
          <div v-for="node in mainNodes" :key="node.key" class="node-item" :class="'node-' + nodeDisplayStatus(node)">
            <div class="node-title">{{ node.title }}</div>
            <a-tag :color="nodeTagColor(nodeDisplayStatus(node))">{{ nodeDisplayLabel(node) }}</a-tag>
            <div class="node-desc">{{ nodeDescription(node) }}</div>
          </div>
        </div>

        <a-tabs>
          <a-tab-pane key="goods" tab="ERP商品">
            <a-descriptions bordered size="small" :column="2">
              <a-descriptions-item label="商品ID">{{ pick(goods, 'id') }}</a-descriptions-item>
              <a-descriptions-item label="商品编号">{{ pick(goods, 'number') }}</a-descriptions-item>
              <a-descriptions-item label="商品名称">{{ pick(goods, 'name') }}</a-descriptions-item>
              <a-descriptions-item label="规格">{{ pick(goods, 'spec') }}</a-descriptions-item>
              <a-descriptions-item label="条码">{{ pick(goods, 'barcode') }}</a-descriptions-item>
              <a-descriptions-item label="状态">{{ pick(goods, 'is_active') }}</a-descriptions-item>
            </a-descriptions>
          </a-tab-pane>

          <a-tab-pane key="traceProduct" tab="追溯档案">
            <a-descriptions bordered size="small" :column="2">
              <a-descriptions-item label="追溯ID">{{ pick(traceProduct, 'id') }}</a-descriptions-item>
              <a-descriptions-item label="关联商品ID">{{ pick(traceProduct, 'product_id') || pick(traceProduct, 'goods_id') }}</a-descriptions-item>
              <a-descriptions-item label="追溯产品">{{ pick(traceProduct, 'product_name') || pick(traceProduct, 'name') }}</a-descriptions-item>
              <a-descriptions-item label="创建时间">{{ pick(traceProduct, 'create_at') || pick(traceProduct, 'create_time') }}</a-descriptions-item>
            </a-descriptions>
          </a-tab-pane>

          <a-tab-pane key="formulas" tab="投料记录">
            <a-table
              rowKey="id"
              size="small"
              :columns="formulaColumns"
              :dataSource="formulas"
              :pagination="false"
            />
          </a-tab-pane>

          <a-tab-pane key="traceability" tab="检验报告">
            <a-table
              rowKey="id"
              size="small"
              :columns="reportColumns"
              :dataSource="reports"
              :pagination="false"
            />
          </a-tab-pane>

          <a-tab-pane key="unsupported" tab="扩展链路">
            <a-table
              rowKey="key"
              size="small"
              :columns="unsupportedColumns"
              :dataSource="unsupportedSteps"
              :pagination="false"
            >
              <template slot="status" slot-scope="text">
                <a-tag color="default">{{ text }}</a-tag>
              </template>
            </a-table>
          </a-tab-pane>
        </a-tabs>
      </template>
    </a-card>
  </div>
</template>

<script>
import { traceChainDetail } from '@/api/goods'

const STATUS_META = {
  complete: { label: '已完整', color: 'green' },
  pending: { label: '缺投料', color: 'orange' },
  incomplete: { label: '缺检验', color: 'gold' },
  unarchived: { label: '未建档', color: 'red' },
  abnormal: { label: '数据异常', color: 'red' },
  unsupported: { label: '暂未接入', color: 'default' },
}

const STEP_LABELS = {
  goods: 'ERP商品',
  trace_product: '追溯档案',
  traceProduct: '追溯档案',
  formulas: '投料记录',
  formula: '投料记录',
  feeding: '投料记录',
  feed: '投料记录',
  traceability: '检验报告',
  inspection: '检验报告',
  report: '检验报告',
  data: '数据异常',
  abnormal: '数据异常',
}

const NODE_DEFAULT_DESCRIPTIONS = {
  goods: {
    complete: 'ERP商品已匹配',
    abnormal: 'ERP商品数据异常，请核对商品状态',
    unsupported: '暂无ERP商品信息',
  },
  trace_product: {
    complete: '追溯档案已建立',
    unarchived: '尚未建立追溯档案',
    unsupported: '暂无追溯档案信息',
  },
  formulas: {
    complete: '已找到投料记录',
    pending: '暂无投料记录，请补充生产投料',
    unsupported: '暂无投料记录',
  },
  traceability: {
    complete: '已找到检验报告',
    incomplete: '暂无检验报告，请补充检验结果',
    unsupported: '暂无检验报告',
  },
}

export default {
  name: 'TraceChain',
  data() {
    return {
      loading: false,
      detail: null,
      searchForm: {
        goods_id: '',
        product_id: '',
        batch: '',
        keyword: '',
      },
      formulaColumns: [
        { title: 'ID', dataIndex: 'id', width: 90 },
        { title: '批次', dataIndex: 'batch', width: 150 },
        { title: '编号', dataIndex: 'number', width: 150 },
        { title: '记录人', dataIndex: 'recorder', width: 120 },
        { title: '备注', dataIndex: 'remark', ellipsis: true },
      ],
      reportColumns: [
        { title: 'ID', dataIndex: 'id', width: 90 },
        { title: '批次', dataIndex: 'date', width: 150 },
        { title: '编号', dataIndex: 'number', width: 150 },
        { title: '检验项', dataIndex: 'title', width: 160 },
        { title: '创建时间', dataIndex: 'create_at', width: 180 },
      ],
      unsupportedColumns: [
        { title: '环节', dataIndex: 'title', width: 160 },
        { title: '状态', dataIndex: 'status', scopedSlots: { customRender: 'status' }, width: 120 },
        { title: '说明', dataIndex: 'reason' },
      ],
    }
  },
  computed: {
    goods() {
      return (this.detail && this.detail.nodes && this.detail.nodes.goods && this.detail.nodes.goods.record) || {}
    },
    traceProduct() {
      return (this.detail && this.detail.nodes && this.detail.nodes.trace_product && this.detail.nodes.trace_product.record) || {}
    },
    formulas() {
      return (this.detail && this.detail.nodes && this.detail.nodes.formulas && this.detail.nodes.formulas.records) || []
    },
    reports() {
      return (this.detail && this.detail.nodes && this.detail.nodes.traceability && this.detail.nodes.traceability.records) || []
    },
    unsupportedSteps() {
      return ((this.detail && this.detail.unsupported) || []).map(item => ({
        ...item,
        key: item.step,
        title: item.label,
        status: '暂未接入',
      }))
    },
    mainNodes() {
      const nodes = (this.detail && this.detail.nodes) || {}
      return [
        { key: 'goods', title: 'ERP商品', ...(nodes.goods || {}) },
        { key: 'trace_product', title: '追溯档案', ...(nodes.trace_product || {}) },
        { key: 'formulas', title: '投料记录', ...(nodes.formulas || {}) },
        { key: 'traceability', title: '检验报告', ...(nodes.traceability || {}) },
      ]
    },
    summaryAlertType() {
      const status = this.detail && this.detail.summary && this.detail.summary.status
      return status === 'complete' ? 'success' : (status === 'abnormal' || status === 'unarchived' ? 'error' : 'warning')
    },
    summaryMessage() {
      const summary = (this.detail && this.detail.summary) || {}
      const meta = STATUS_META[summary.status] || {}
      return meta.label || summary.status || '链路查询'
    },
    summaryDescription() {
      const summary = (this.detail && this.detail.summary) || {}
      const missing = Array.isArray(summary.missing_steps) && summary.missing_steps.length
        ? `缺失环节：${summary.missing_steps.map(this.stepLabel).join('、')}`
        : ''
      const batch = summary.latest_batch ? `最新批次：${summary.latest_batch}` : ''
      return [summary.conclusion, batch, missing].filter(Boolean).join('；')
    },
  },
  created() {
    this.searchForm = { ...this.searchForm, ...this.$route.query }
    if (this.searchForm.goods_id || this.searchForm.product_id || this.searchForm.batch || this.searchForm.keyword) {
      this.loadDetail()
    }
  },
  methods: {
    async loadDetail() {
      const params = {}
      Object.keys(this.searchForm).forEach(key => {
        const value = this.searchForm[key]
        if (value !== undefined && value !== null && String(value).trim() !== '') params[key] = value
      })
      if (!params.goods_id && !params.product_id && !params.keyword) {
        this.$message.warning('请先输入商品ID、追溯ID或关键词')
        return
      }
      this.loading = true
      try {
        this.detail = await traceChainDetail(params)
      } catch (err) {
        this.detail = null
        this.$message.error((err && err.message) || '链路查询失败')
      } finally {
        this.loading = false
      }
    },
    nodeTagColor(status) {
      return (STATUS_META[status] && STATUS_META[status].color) || 'default'
    },
    nodeDisplayStatus(node) {
      if (!node) return 'unsupported'
      if (node.status && STATUS_META[node.status]) return node.status
      const summary = (this.detail && this.detail.summary) || {}
      const missingSteps = Array.isArray(summary.missing_steps) ? summary.missing_steps : []
      if (node.key === 'traceability' && missingSteps.some(step => ['inspection', 'traceability', 'report'].includes(step))) {
        return 'incomplete'
      }
      if (node.key === 'formulas' && missingSteps.some(step => ['feeding', 'feed', 'formula', 'formulas'].includes(step))) {
        return 'pending'
      }
      if (node.key === 'trace_product' && missingSteps.some(step => ['trace_product', 'traceProduct'].includes(step))) {
        return 'unarchived'
      }
      if (node.key === 'goods' && missingSteps.includes('goods')) {
        return 'abnormal'
      }
      return node.status || 'unsupported'
    },
    nodeDisplayLabel(node) {
      const status = this.nodeDisplayStatus(node)
      if (status !== 'complete' && STATUS_META[status]) return STATUS_META[status].label
      return (node && node.label) || (node && node.title) || '-'
    },
    nodeDescription(node) {
      if (node && node.description) return node.description
      const status = this.nodeDisplayStatus(node)
      const descriptions = (node && NODE_DEFAULT_DESCRIPTIONS[node.key]) || {}
      return descriptions[status] || descriptions.unsupported || '暂无链路信息'
    },
    stepLabel(step) {
      return STEP_LABELS[step] || step || '未知环节'
    },
    pick(row, key) {
      if (!row || row[key] === undefined || row[key] === null || row[key] === '') return '-'
      return row[key]
    },
  },
}
</script>

<style scoped>
.trace-chain-page {
  padding: 0;
}

.search-form {
  margin-bottom: 16px;
}

.summary-alert {
  margin-bottom: 16px;
}

.node-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.node-item {
  min-height: 112px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 12px;
  background: #fff;
}

.node-complete {
  border-color: #b7eb8f;
}

.node-pending,
.node-incomplete {
  border-color: #ffd591;
}

.node-unarchived,
.node-abnormal {
  border-color: #ffa39e;
}

.node-title {
  font-weight: 600;
  margin-bottom: 8px;
}

.node-desc {
  margin-top: 8px;
  color: #8c8c8c;
  font-size: 12px;
  line-height: 1.5;
}
</style>
